import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { Device } from "@twilio/voice-sdk";
import { Phone, PhoneOff, PhoneIncoming, Mic, MicOff } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const VoiceCtx = createContext(null);
export const useVoice = () => useContext(VoiceCtx) || {};

// Provider VoIP in-app : enregistre un Device Twilio (pour recevoir) dès que l'utilisateur est connecté.
export const VoiceProvider = ({ children }) => {
  const { user } = useAuth();
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const incomingFromRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | calling | incoming | active
  const [peerName, setPeerName] = useState("");
  const [muted, setMuted] = useState(false);

  const reset = useCallback(() => {
    callRef.current = null; setStatus("idle"); setPeerName(""); setMuted(false);
  }, []);

  const buildDevice = useCallback(async () => {
    const { data } = await api.get("/voice-sdk/token");
    if (!data?.token) throw new Error("no-token");
    const device = new Device(data.token, { codecPreferences: ["opus", "pcmu"] });
    device.on("error", (e) => console.warn("twilio device error", e?.code, e?.message));
    device.on("incoming", (call) => {
      callRef.current = call;
      let cname = "Appel entrant", cid = null;
      try { cname = call.customParameters?.get("callerName") || "Appel entrant"; cid = call.customParameters?.get("callerId") || null; } catch {}
      incomingFromRef.current = { id: cid, name: cname };
      setPeerName(cname);
      setStatus("incoming");
      call.on("disconnect", reset);
      call.on("cancel", reset);
      call.on("reject", reset);
    });
    device.on("tokenWillExpire", async () => {
      try { const r = await api.get("/voice-sdk/token"); device.updateToken(r.data.token); } catch {}
    });
    await device.register();
    return device;
  }, [reset]);

  // Ré-initialise le Device à la demande (dans le geste de clic) si l'enregistrement au montage a échoué (Safari).
  const ensureDevice = useCallback(async () => {
    if (deviceRef.current) return deviceRef.current;
    const device = await buildDevice();
    deviceRef.current = device;
    return device;
  }, [buildDevice]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const device = await buildDevice();
        if (cancelled) { try { device.destroy(); } catch {} return; }
        deviceRef.current = device;
      } catch (e) { console.warn("voice device init failed", e?.message); }
    })();
    return () => { cancelled = true; const d = deviceRef.current; deviceRef.current = null; try { d && d.destroy(); } catch {} };
  }, [user?.id, buildDevice]);

  const call = useCallback(async (targetUserId, name) => {
    try {
      // 1) Micro D'ABORD, dans le geste de clic (indispensable sur Safari pour débloquer l'audio).
      try { await navigator.mediaDevices.getUserMedia({ audio: true }); }
      catch { return toast.error("Micro bloqué. Autorisez le microphone pour ce site puis réessayez."); }
      // 2) Autorisation métier (course active, sauf admin) + réveil push du destinataire.
      const auth = await api.post("/voice-sdk/authorize", { target_user_id: targetUserId });
      if (!auth.data?.allowed) return toast.error("Appel non autorisé.");
      // 3) S'assurer que le Device est prêt (re-init si le montage a échoué, ex. Safari).
      let device;
      try { device = await ensureDevice(); }
      catch { return toast.error("Connexion aux appels impossible. Vérifiez votre réseau et réessayez."); }
      setPeerName(name || "Appel"); setStatus("calling");
      const isAdmin = (user?.roles || []).includes("admin");
      const c = await device.connect({ params: {
        To: auth.data.to_identity,
        CallerId: user?.id || "",
        CallerName: isAdmin ? "Administration Mboa Go" : (user?.name || "Votre contact"),
      } });
      callRef.current = c;
      let answered = false;
      c.on("accept", () => { answered = true; setStatus("active"); });
      c.on("disconnect", async () => {
        if (answered) { reset(); return; }
        // Distingue REFUS (message clair) de HORS LIGNE via le backend.
        let rejected = false, callee = name;
        for (let i = 0; i < 3 && !rejected; i++) {
          await new Promise((r) => setTimeout(r, i === 0 ? 600 : 900));
          try { const r = await api.get(`/voice-sdk/last-reject?peer=${targetUserId}`); if (r.data?.rejected) { rejected = true; callee = r.data.callee_name || name; } } catch {}
        }
        if (rejected) toast.error(`${callee || "Le destinataire"} a refusé votre appel.`);
        else toast(`${name || "Le destinataire"} est hors ligne — une notification l'invite à ouvrir l'app pour vous rappeler.`, { duration: 6000 });
        reset();
      });
      c.on("cancel", reset);
      c.on("reject", () => { toast.error(`${name || "Le destinataire"} a refusé votre appel.`); reset(); });
      c.on("error", () => { toast.error("Problème de connexion à l'appel."); reset(); });
    } catch (e) { toast.error(apiError(e)); reset(); }
  }, [reset, user, ensureDevice]);

  const accept = useCallback(() => {
    if (callRef.current) { callRef.current.accept(); setStatus("active"); }
  }, []);
  const hangup = useCallback(() => {
    const c = callRef.current;
    const wasIncoming = status === "incoming";
    const from = incomingFromRef.current;
    if (c) { try { wasIncoming ? c.reject() : c.disconnect(); } catch {} }
    if (wasIncoming && from?.id) {
      // B) Prévient l'appelant du refus + propose au chauffeur de rappeler.
      api.post("/voice-sdk/reject", { caller_id: from.id }).catch(() => {});
      toast(`Vous avez refusé l'appel de ${from.name || "votre contact"}.`, {
        duration: 8000,
        action: { label: "Rappeler", onClick: () => call(from.id, from.name || "Contact") },
      });
    }
    reset();
  }, [status, reset, call]);
  const toggleMute = useCallback(() => {
    const c = callRef.current;
    if (c) { const m = !muted; c.mute(m); setMuted(m); }
  }, [muted]);

  return (
    <VoiceCtx.Provider value={{ call, status }}>
      {children}
      {status !== "idle" && (
        <div data-testid="call-overlay" className="fixed bottom-5 right-5 z-[9999] w-72 rounded-2xl bg-[#1A1A1A] text-white shadow-2xl border border-white/10 p-4">
          <div className="flex items-center gap-2 text-sm text-white/70">
            {status === "incoming" ? <PhoneIncoming className="w-4 h-4 text-[#F26419]" /> : <Phone className="w-4 h-4 text-[#F26419]" />}
            {status === "calling" ? "Appel en cours…" : status === "incoming" ? "Appel entrant" : "En communication"}
          </div>
          <div className="mt-1 font-head font-black text-lg truncate">{peerName}</div>
          <div className="mt-4 flex items-center gap-2">
            {status === "incoming" && (
              <button data-testid="call-accept-btn" onClick={accept} className="flex-1 h-11 rounded-xl bg-[#16a34a] hover:bg-[#15803d] flex items-center justify-center gap-1.5 font-bold"><Phone className="w-4 h-4" /> Répondre</button>
            )}
            {status === "active" && (
              <button data-testid="call-mute-btn" onClick={toggleMute} className="h-11 px-3 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center">{muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}</button>
            )}
            <button data-testid="call-hangup-btn" onClick={hangup} className="flex-1 h-11 rounded-xl bg-[#D32F2F] hover:bg-[#b71c1c] flex items-center justify-center gap-1.5 font-bold"><PhoneOff className="w-4 h-4" /> {status === "incoming" ? "Refuser" : "Raccrocher"}</button>
          </div>
        </div>
      )}
    </VoiceCtx.Provider>
  );
};

// Bouton réutilisable "Appeler ..."
export const CallButton = ({ targetUserId, name, label = "Appeler", className = "", testId }) => {
  const { call } = useVoice();
  return (
    <button data-testid={testId} onClick={() => call && call(targetUserId, name)}
      className={className || "inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-[#16a34a] hover:bg-[#15803d] text-white font-bold text-sm transition-colors"}>
      <Phone className="w-4 h-4" /> {label}
    </button>
  );
};
